import React from 'react';
import Link from '@mui/material/Link';
import Popper from '@mui/material/Popper';
import classNames from 'classnames';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import { useFeature } from 'flagged';
import PropTypes from 'prop-types';
import Avatar from '@/components/common/Avatar';
import CustomDialog from '@/components/common/CustomDialog';
import LastInteraction from '@/components/common/LastInteraction';
import Loader from '@/components/common/Loader';
import RouterLink from '@/components/common/RouterLink';
import ConstituentType from '@/constants/ConstituentType';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import FeatureFlags from '@/constants/system/FeatureFlags';
import { EmailIcon, PhoneIcon } from '@/lib/configs/donor-icons';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import translate from '@/lib/intl/translate';
import {
  IWaveScoreEvaluation,
  useConstituentIWaveData,
} from '@/modules/IWaveIntegration';
import {
  getPrimaryEmail,
  getPrimaryPhoneNumber,
} from '@/utils/constituentUtils';
import { formatCurrency } from '@/utils/currency';
import getInitials from '@/utils/getInitials';
import ConstituentCardItemActionsAssign from './ConstituentCardItemActionsAssign';
import ConstituentIconTiles from './ConstituentIconTiles';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import ConstituentLifecycleBoardList from './ConstituentLifecycleBoardList';

const ConstituentActions = Object.freeze({
  ASSIGN: 'assign',
});

function ConstituentHoverCard({
  anchorEl,
  className,
  constituentId,
  onClose,
  open,
}) {
  const hasIsDeceased = useFeature(FeatureFlags.IS_DECEASED);
  const hasIWave = useFeature(FeatureFlags.IWAVE);
  const [action, setAction] = React.useState(null);
  const { data: constituent } = useConstituent(constituentId);
  const { iwaveProfile } = useConstituentIWaveData(constituentId);

  const {
    email,
    initials,
    lastDonation,
    lifetimeSum,
    openTasks,
    overdueTasks,
    phone,
  } = React.useMemo(() => {
    if (!constituent) return {};
    const now = new Date();
    return {
      email: getPrimaryEmail(constituent.emails),
      initials: getInitials(
        constituent.displayName,
        constituent.constituentType === ConstituentType.PERSON,
      ),
      lastDonation: constituent.donor?.lastDonationSum
        ? `${formatCurrency(
            constituent.donor.lastDonationSum,
            true,
          )} - ${format(new Date(constituent.donor.lastDonationDate), 'PP')}`
        : 'N/A',
      lifetimeSum: formatCurrency(constituent.donor?.lifetimeSum, true),
      openTasks: constituent.incompleteTasks?.length,
      overdueTasks: (constituent.incompleteTasks || []).filter(({ dueDate }) =>
        isBefore(new Date(dueDate), now),
      ).length,
      phone: getPrimaryPhoneNumber(constituent.phones),
    };
  }, [constituent]);

  const closeModal = () => setAction(null);

  const modal = React.useMemo(() => {
    switch (action) {
      case ConstituentActions.ASSIGN:
        return (
          <ConstituentCardItemActionsAssign
            constituent={constituent}
            onClose={closeModal}
          />
        );
      default:
        return null;
    }
  }, [action]);

  return (
    <Popper placement="bottom-start" anchorEl={anchorEl} open={open}>
      <div
        className={classNames(
          className,
          'block w-80 max-w-full rounded-lg bg-white px-4 pt-6 pb-3 shadow',
        )}
        onMouseLeave={onClose}
      >
        {constituent ? (
          <>
            <div className="mb-2 flex items-center">
              <Avatar src={constituent.avatar} size="72px">
                {initials}
              </Avatar>
              <div className="ml-4 grid gap-1">
                <div className="type-h3">{constituent.displayName}</div>
                {hasIsDeceased && constituent.isDeceased && (
                  <div className="text-sm font-normal text-grey-900">
                    {translate('DECEASED_LABEL')}
                  </div>
                )}
                <LastInteraction
                  date={constituent.lastInteractionDate}
                  showEmpty
                />
                <ConstituentIconTiles constituent={constituent} />
              </div>
            </div>
            {(email || phone) && (
              <div className="grid gap-2 border-b border-grey-300 pb-4 pt-2 type-body3">
                {email && (
                  <div className="flex items-center">
                    {EmailIcon}
                    <span className="ml-2 mt-1">{email}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center">
                    {PhoneIcon}
                    <span className="ml-2 mt-1">{phone}</span>
                  </div>
                )}
              </div>
            )}
            <div className="grid gap-2 py-4 type-body3">
              <div>
                {translate('CONSTITUENT_ASSIGNED_TO')}:{' '}
                <Link
                  component="button"
                  onClick={() => setAction(ConstituentActions.ASSIGN)}
                >
                  {constituent.developers?.length
                    ? constituent.developers
                        .map(({ displayName }) => displayName)
                        .join(', ')
                    : translate('CONSTITUENT_UNASSIGNED')}
                </Link>
              </div>
              <ConstituentLifecycleBoardList
                constituentId={constituentId}
                compact
              />
              {!!openTasks && (
                <div>
                  {translate('TASKS')}:{' '}
                  <RouterLink to={AuthRoutes.TASKS}>
                    {translate('TASKS_COUNT_OPEN', {
                      count: openTasks,
                    })}
                    {!!overdueTasks && (
                      <>
                        <span className="font-normal text-text-secondary">
                          {' | '}
                        </span>
                        <span className="font-semibold text-error-main">
                          {translate('TASKS_COUNT_OVERDUE', {
                            count: overdueTasks,
                          })}
                        </span>
                      </>
                    )}
                  </RouterLink>
                </div>
              )}
            </div>
            <div className="-mx-2 grid gap-2 rounded-lg bg-grey-100 p-4 type-body3">
              <div>
                {translate('CONSTITUENT_LAST_CONTRIBUTION')}: {lastDonation}
              </div>
              <div>
                {translate('CONSTITUENT_LIFETIME_GIVING')}: {lifetimeSum}
              </div>
            </div>

            {hasIWave && iwaveProfile?.score && (
              <div className="-mx-2 mt-4 rounded-lg bg-grey-100 p-4 type-body3">
                {translate('IWAVE_HOVERCARD_EVALUATION')}{' '}
                <IWaveScoreEvaluation score={iwaveProfile.score} withColor />
              </div>
            )}

            <CustomDialog
              fullWidth
              maxWidth="md"
              open={!!modal}
              onClose={closeModal}
              showDialogActions={false}
            >
              {modal}
            </CustomDialog>
          </>
        ) : (
          <Loader className="mb-16 mt-12" isCentered />
        )}
      </div>
    </Popper>
  );
}

ConstituentHoverCard.propTypes = {
  anchorEl: PropTypes.oneOf([PropTypes.element, PropTypes.instanceOf(Element)]),
  className: PropTypes.string,
  constituentId: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

ConstituentHoverCard.defaultProps = {
  anchorEl: null,
  className: '',
  constituentId: null,
  onClose: () => {},
  open: false,
};

export default ConstituentHoverCard;
